'use client';

import { formatDateTime } from '@pickleballinc/lib/format-dates';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import clsx from 'clsx';
import { useMemo, useState } from 'react';

import CustomLink from '../../ui/custom-link';
import { LEAGUES_PLACEHOLDER_IMAGE } from '../utils/constants';
import { formatCurrency } from '../utils/formatCurrency';
import { isAbsoluteUrl } from '../utils/isAbsoluteUrl';

export function renderPlayerLeagueCardLabels(league: LeagueCardProps): string {
	if (league.isCanceled) {
		return 'canceled';
	} else if (league.isLocked === true) {
		return 'completed';
	} else {
		if (!league.isRegistrationClosed) {
			if (parseFloat(league.price) > 0) {
				return 'price';
			} else {
				return 'free';
			}
		} else {
			return 'registration-closed';
		}
	}
}

export interface LeagueCardProps {
	id?: string;
	dateFrom?: string;
	dateTo?: string;
	isCanceled?: boolean;
	isCostPerEvent?: boolean;
	currency: string;
	location: string;
	isRegistrationClosed?: boolean;
	registrationDateClosed?: Date | undefined;
	logo: string;
	price: string;
	registrationCount: number;
	slug?: string;
	status?: string;
	title: string;
	isAdvertiseOnly?: boolean;
	hideRegisteredPlayers?: boolean;
	className?: string;
	logoClassName?: string;
	startTime?: string;
	shouldHideBadge?: boolean;
	clubTitle?: string;
	isLocked?: boolean;
	shouldHideStartTime?: boolean;
	formatTag: string;
	/**
	 * Override the click-through URL. When set, the card wraps itself in a `CustomLink` pointing at this
	 * href instead of the default (today the card has no default link wrapper). Mirrors
	 * `TournamentCard.customCardHref` so callers like the listing-ad rotator can drive both cards through
	 * the same prop.
	 */
	customCardHref?: string;
	/**
	 * When paired with `customCardHref`, opens the click-through in a new tab (`target="_blank"` + safe
	 * `rel="noopener noreferrer"`). No effect when `customCardHref` is not set.
	 */
	openInNewTab?: boolean;
	/**
	 * Render the click-through as a plain `<a>` (full document navigation) instead of a Next `<Link>`.
	 * Set by the listing-ad rotator: an ad's URL carries Everflow parameters that the landing middleware
	 * has to see, and Next would route a same-origin URL client-side and skip it.
	 */
	nativeAnchor?: boolean;
	/**
	 * Suppresses the entire bottom row (status badge + registration count). Use when the surrounding
	 * surface (e.g. listing-ad rotator on the clubs search page) wants the card to collapse to title +
	 * club + location only — sized to match neighbouring `MyClubCard`s.
	 */
	hideBottomRow?: boolean;
	/**
	 * Suppresses the location row. Mirrors `hideBottomRow` — used by the clubs listing-ad rotator to
	 * shrink the card vertically further.
	 */
	hideLocation?: boolean;
}

export const LeagueCard = (props: LeagueCardProps) => {
	const { customCardHref, openInNewTab, nativeAnchor } = props;
	const newStatus = props.status ? props.status : renderPlayerLeagueCardLabels(props);

	const card = <LeagueCardComponent {...props} status={newStatus} />;
	// `h-full w-full` on the link is required so the inner card's `!size-full` resolves against a parent
	// with a defined height — listing-ad rotator wraps the card in a grid item with `h-full`, and without
	// filling the link the card collapses to content height and breaks alignment with neighbors. Safe in
	// non-ad contexts too: block elements with `h-full` inside grid cells just stretch to the cell.
	return customCardHref ? (
		<CustomLink
			href={customCardHref}
			className="block h-full w-full"
			target={openInNewTab ? '_blank' : undefined}
			rel={openInNewTab ? 'noopener noreferrer' : undefined}
			native={nativeAnchor}
		>
			{card}
		</CustomLink>
	) : (
		card
	);
};

const LeagueCardComponent = ({
	dateFrom,
	dateTo,
	logo,
	currency,
	formatTag,
	location,
	price,
	registrationCount,
	status,
	title,
	hideRegisteredPlayers,
	isAdvertiseOnly,
	className,
	logoClassName,
	startTime,
	shouldHideBadge,
	clubTitle,
	shouldHideStartTime,
	hideBottomRow,
	hideLocation
}: LeagueCardProps) => {
	// Using parseZone() to show dates in League's timezone
	const formattedFromDate = formatDateTime(dateFrom, { parseZone: true });
	const formattedToDate = formatDateTime(dateTo, { parseZone: true });

	const [isPlaceholderImage, setIsPlaceholderImage] = useState(false);

	const getImageToDisplay = useMemo(() => {
		if (logo) {
			const lowerCasedLogo = logo.toLowerCase();

			// Brackets `/uploads/...` paths and the full media-service URLs that logos saved
			// through manage produce both go through `getCDNURL` — it resolves the CDN from
			// the host, so the two cases no longer need separate branches.
			if (lowerCasedLogo.includes('/uploads/clubs/') || lowerCasedLogo.includes('/uploads/events/') || isAbsoluteUrl(logo)) {
				return getCDNURL(logo, 566, 384);
			}
		}

		setIsPlaceholderImage(true);
		return getCDNURL(LEAGUES_PLACEHOLDER_IMAGE, 566, 384);
	}, [logo]);

	return (
		<div
			className={clsx(
				className,
				'group relative flex cursor-pointer !size-full w-full min-[500px]:min-w-[224px] min-[500px]:max-w-[286px] flex-col rounded-xl border bg-white'
			)}
		>
			<div
				className={clsx(
					logoClassName,
					'relative flex items-center w-full justify-center overflow-hidden rounded-t-xl border-b border-b-gray-200 bg-white',
					isPlaceholderImage && 'py-4'
				)}
				style={{ aspectRatio: '16 / 9' }}
			>
				<img src={getImageToDisplay} alt={title} className={clsx(logoClassName, 'block h-full rounded-t-xl object-contain')} />
			</div>
			<div className="flex flex-1 flex-col rounded-b-xl justify-between !gap-2 p-2 sm:p-4 pt-3 relative z-20 group-hover:bg-gray-50">
				<div className="flex flex-col gap-2">
					<div className="!line-clamp-2 block text-sm font-medium min-h-[2.5rem] text-gray-900" title={title}>
						{title}
					</div>
					{clubTitle ? <div className="!line-clamp-2 sm:text-sm text-xs text-gray-600">{clubTitle}</div> : null}

					{!hideLocation ? <div className="!line-clamp-2 sm:text-sm text-xs text-gray-600">{location}</div> : null}

					{dateFrom && dateTo ? (
						<div className="!line-clamp-2 sm:text-sm text-xs text-gray-600">{`${formattedFromDate} - ${formattedToDate}`}</div>
					) : null}
					{!shouldHideStartTime && <div className="!line-clamp-2 sm:text-sm text-xs text-gray-600">{startTime}</div>}
				</div>
				{!hideBottomRow && (status || registrationCount) ? (
					<div
						className={`mt-2 flex flex-row flex-wrap min-[450px]:items-center items-start gap-1 ${shouldHideBadge ? 'justify-end' : 'justify-between'}`}
					>
						{status === 'completed' && !shouldHideBadge && (
							<LeagueCardBadge type="closed">
								<p className="text-xs min-[450px]:text-sm font-semibold">Completed</p>
							</LeagueCardBadge>
						)}
						{status === 'registration-closed' && !shouldHideBadge && (
							<LeagueCardBadge type="default">
								<p className="text-xs min-[450px]:text-sm font-semibold">View</p>
							</LeagueCardBadge>
						)}
						{status === 'price' && !shouldHideBadge && (
							<LeagueCardBadge type="default">
								<p className="text-xs min-[450px]:text-sm font-semibold">{formatCurrency(price, currency, formatTag)}</p>
							</LeagueCardBadge>
						)}
						{status === 'free' && !shouldHideBadge && (
							<LeagueCardBadge type="default">
								<p className="text-xs min-[450px]:text-sm font-semibold">Register</p>
							</LeagueCardBadge>
						)}
						{!hideRegisteredPlayers &&
							(isAdvertiseOnly ? (
								<div className="text-xs flex text-gray-600 items-center" style={{ minHeight: '1.75rem' }}>
									Advertised
								</div>
							) : (
								<div
									className="text-xs flex text-gray-600 items-center"
									style={{ minHeight: '1.75rem' }}
								>{`${registrationCount} player${registrationCount != 1 ? 's' : ''}`}</div>
							))}
					</div>
				) : null}
			</div>
		</div>
	);
};

export type LeagueCardBadgeType = 'default' | 'closed';

export const LeagueCardBadge = ({
	children,
	className,
	type
}: {
	children: React.ReactNode;
	className?: string;
	type: LeagueCardBadgeType;
	size?: 'sm' | 'md';
}) => {
	const classnames = clsx(
		'px-2 py-1 text-center text-xs font-semibold rounded-md',
		{
			'bg-success-700 text-white': type === 'default',
			'bg-gray-700 text-white': type === 'closed'
		},
		className
	);

	return <div className={classnames}>{children}</div>;
};
